import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import {
  getTasks,
  createTask,
  toggleTaskStatus,
  deleteTask
} from '../../../services/dashboardService';
import { TaskItem, TaskFormData } from '../../../types/dashboard';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

const TasksWidget: React.FC = () => {
  const [data, setData] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState<TaskFormData>({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await getTasks();
      setData(result);
      setError(null);
    } catch (err) {
      setError('فشل في تحميل المهام');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTask = async (taskId: string) => {
    try {
      await toggleTaskStatus(taskId);
      await fetchData();
    } catch (err) {
      console.error('Error toggling task:', err);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذه المهمة؟')) {
      try {
        await deleteTask(taskId);
        await fetchData();
      } catch (err) {
        console.error('Error deleting task:', err);
      }
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim()) {
      alert('يرجى إدخال عنوان المهمة');
      return;
    }

    try {
      await createTask(newTask);
      setDialogOpen(false);
      setNewTask({
        title: '',
        description: '',
        priority: 'medium',
        dueDate: ''
      });
      await fetchData();
    } catch (err) {
      console.error('Error creating task:', err);
      alert('فشل في إضافة المهمة');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return { bg: '#fee', color: '#e74c3c', label: 'عاجل' };
      case 'medium':
        return { bg: '#fef5e7', color: '#f39c12', label: 'متوسط' };
      case 'low':
        return { bg: '#eafaf1', color: '#27ae60', label: 'عادي' };
      default:
        return { bg: '#f8f9fa', color: '#95a5a6', label: 'غير محدد' };
    }
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', p: 3, minHeight: 300 }}>
        <Typography color="error">{error}</Typography>
      </Card>
    );
  }

  const pendingTasks = data.filter(task => task.status !== 'completed');

  return (
    <>
      <Card
        sx={{
          height: '100%',
          borderTop: '4px solid #27ae60',
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '2px solid #f0f0f0' }}>
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 24,
                background: 'linear-gradient(135deg, #0ba360 0%, #3cba92 100%)',
                color: 'white',
                mr: 2
              }}
            >
              ✅
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 0.5 }}>
                المهام
              </Typography>
              <Typography variant="body2" sx={{ color: '#95a5a6' }}>
                <Chip 
                  label={`${pendingTasks.length} مهام`} 
                  size="small" 
                  sx={{ 
                    bgcolor: '#eafaf1', 
                    color: '#27ae60',
                    fontWeight: 'bold'
                  }} 
                /> نشطة
              </Typography>
            </Box>
          </Box>

          {/* Task List */}
          {data.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h3" sx={{ opacity: 0.3, mb: 2 }}>📝</Typography>
              <Typography color="textSecondary">لا توجد مهام حالياً</Typography>
            </Box>
          ) : (
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {data.map((task) => {
                const priorityStyle = getPriorityColor(task.priority);
                return (
                  <ListItem
                    key={task._id}
                    sx={{
                      bgcolor: '#f8f9fa',
                      borderRadius: 2,
                      mb: 1,
                      opacity: task.status === 'completed' ? 0.5 : 1,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: '#e9ecef'
                      }
                    }}
                  >
                    <Checkbox
                      checked={task.status === 'completed'}
                      onChange={() => handleToggleTask(task._id)}
                      sx={{ mr: 1 }}
                    />
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 'bold',
                            color: '#2c3e50',
                            textDecoration: task.status === 'completed' ? 'line-through' : 'none'
                          }}
                        >
                          {task.title}
                        </Typography>
                      }
                      secondary={
                        task.description && (
                          <Typography variant="caption" sx={{ color: '#95a5a6' }}>
                            {task.description}
                          </Typography>
                        )
                      }
                    />
                    <Chip
                      label={priorityStyle.label}
                      size="small"
                      sx={{
                        bgcolor: priorityStyle.bg,
                        color: priorityStyle.color,
                        fontWeight: 'bold',
                        mr: 1
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteTask(task._id)}
                      sx={{ color: '#e74c3c' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItem>
                );
              })}
            </List>
          )}

          {/* Add Task Button */}
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{
              mt: 2,
              background: 'linear-gradient(135deg, #0ba360 0%, #3cba92 100%)',
              color: 'white',
              fontWeight: 'bold',
              py: 1.5,
              '&:hover': {
                background: 'linear-gradient(135deg, #3cba92 0%, #0ba360 100%)',
                transform: 'scale(1.02)'
              }
            }}
          >
            إضافة مهمة جديدة
          </Button>
        </CardContent>
      </Card>

      {/* Add Task Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>إضافة مهمة جديدة</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="عنوان المهمة"
              fullWidth
              required
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
            />
            <TextField
              label="الوصف"
              fullWidth
              multiline
              rows={3}
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>الأولوية</InputLabel>
              <Select
                value={newTask.priority}
                label="الأولوية"
                onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as any })}
              >
                <MenuItem value="low">عادي</MenuItem>
                <MenuItem value="medium">متوسط</MenuItem>
                <MenuItem value="high">عاجل</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="تاريخ الاستحقاق"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={newTask.dueDate}
              onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>إلغاء</Button>
          <Button onClick={handleAddTask} variant="contained" color="primary">
            إضافة
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TasksWidget;